import React, { useMemo } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import {
  Tooltip,
  Button,
  Table,
  Badge,
  Icon,
  Card,
  Row,
  Col,
} from 'antd';

const tableStyles = `
@keyframes icon-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}
@keyframes fade-in {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes slide-in-left {
  from { opacity: 0; transform: translateX(-20px); }
  to { opacity: 1; transform: translateX(0); }
}

.po-table-container {
  animation: fade-in 0.4s ease both;
  width: 100%;
  overflow-x: auto;
}

.po-action-btn {
  width: 28px !important;
  height: 28px !important;
  min-width: 28px !important;
  padding: 0 !important;
  border-radius: 7px !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  font-size: 13px !important;
  transition: all 0.2s ease !important;
  border: none !important;
}
.po-action-btn:hover {
  transform: translateY(-2px) !important;
}
.po-btn-invoice {
  background: #e6f7ff !important;
  color: #1890ff !important;
}
.po-btn-invoice:hover {
  background: #1890ff !important;
  color: #fff !important;
  box-shadow: 0 4px 10px rgba(24,144,255,0.35) !important;
}
.po-btn-items {
  background: #f6ffed !important;
  color: #52c41a !important;
}
.po-btn-items:hover {
  background: #52c41a !important;
  color: #fff !important;
  box-shadow: 0 4px 10px rgba(82,196,26,0.35) !important;
}

.ant-table-row-stripe td {
  background: #f7f9ff !important;
}

.po-stats-card {
  border-radius: 10px;
  border: 1px solid #e6f0ff;
  background: linear-gradient(135deg, #fff, #fafcff);
  transition: all 0.3s ease;
  animation: slide-in-left 0.4s ease both;
}
.po-stats-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(24,144,255,0.12);
}

.po-stats-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}
.po-stats-icon:hover {
  transform: scale(1.05);
}

/* Icon pulse animations - different delays for staggered effect */
.po-stats-card:nth-child(1) .po-stats-icon {
  animation: icon-pulse 1.6s ease-in-out infinite;
  animation-delay: 0s;
}
.po-stats-card:nth-child(2) .po-stats-icon {
  animation: icon-pulse 1.6s ease-in-out infinite;
  animation-delay: 0.3s;
}
.po-stats-card:nth-child(3) .po-stats-icon {
  animation: icon-pulse 1.6s ease-in-out infinite;
  animation-delay: 0.6s;
}
.po-stats-card:nth-child(4) .po-stats-icon {
  animation: icon-pulse 1.6s ease-in-out infinite;
  animation-delay: 0.9s;
}

/* Table header styling with colors - normal text weight */
.ant-table-thead > tr > th {
  white-space: nowrap !important;
  font-weight: 500 !important;
  background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%) !important;
  color: #ffffff !important;
  padding: 14px 8px !important;
  font-size: 13px !important;
  letter-spacing: 0.3px !important;
  border-bottom: none !important;
}

.ant-table-thead > tr > th::before {
  display: none !important;
}

/* Header hover effect */
.ant-table-thead > tr > th:hover {
  background: linear-gradient(135deg, #096dd9 0%, #0050b3 100%) !important;
  cursor: pointer;
}

/* Header icons and filters color */
.ant-table-thead > tr > th .anticon,
.ant-table-thead > tr > th .ant-table-filter-icon,
.ant-table-thead > tr > th .ant-table-column-sorter {
  color: #ffffff !important;
}

.ant-table-thead > tr > th .ant-table-column-sorter-up.active,
.ant-table-thead > tr > th .ant-table-column-sorter-down.active {
  color: #ffd666 !important;
}

/* Table body cells */
.ant-table-tbody > tr > td {
  padding: 12px 8px !important;
  font-size: 13px !important;
}

/* Ensure table doesn't overflow on small screens */
@media (max-width: 768px) {
  .ant-table {
    min-width: 800px;
  }
}
`;

const PurchaseOrderTable = (props) => {
  const {
    poList,
    supplierOption,
    openModal,
  } = props;

  // Calculate statistics based ONLY on poList table data
  const stats = useMemo(() => {
    if (!poList || poList.length === 0) {
      return {
        total: 0,
        totalQuantity: 0,
        phpCount: 0,
        usdCount: 0,
      };
    }

    const total = poList.length;
    const totalQuantity = poList.reduce((sum, po) => sum + (po.totalPoQuantity || 0), 0);
    const phpCount = poList.filter(po => po.currency === 'PHP').length;
    const usdCount = poList.filter(po => po.currency === 'USD').length;

    return {
      total,
      totalQuantity,
      phpCount,
      usdCount,
    };
  }, [poList]);

  const columns = useMemo(() => [
    {
      key: 'viewPurchaseOrderInvoice',
      width: 60,
      align: 'center',
      title: '',
      render: (data) => (
        <Tooltip placement="right" title="View invoices">
          <Button
            size="small"
            icon="file-text"
            onClick={() => openModal(data, 'invoice')}
            className="po-action-btn po-btn-invoice"
          />
        </Tooltip>
      ),
    },
    {
      key: 'viewPurchaseOrderItems',
      width: 60,
      align: 'center',
      title: '',
      render: (data) => (
        <Tooltip placement="right" title="View items">
          <Button
            size="small"
            icon="profile"
            onClick={() => openModal(data, 'item')}
            className="po-action-btn po-btn-items"
          />
        </Tooltip>
      ),
    },
    {
      key: 'supplier',
      title: 'SUPPLIER',
      dataIndex: 'supplier',
      align: 'center',
      filters: supplierOption.map((supplier) => ({
        text: supplier.supplierName,
        value: supplier.id,
      })),
      onFilter: (value, record) => record.supplierId === value,
      sorter: (a, b) => (a.supplier || '').localeCompare(b.supplier || ''),
      render: (text) => (text ? text.toUpperCase() : '-'),
    },
    {
      key: 'poNum',
      title: 'PURCHASE ORDER',
      dataIndex: 'poNum',
      align: 'center',
      sorter: (a, b) => (a.poNum || '').localeCompare(b.poNum || ''),
      render: (text) => (
        <span style={{ fontWeight: 600, color: '#1890ff' }}>
          {text ? text.toUpperCase() : '-'}
        </span>
      ),
    },
    {
      key: 'prNumbers',
      title: 'PURCHASE REQUEST',
      dataIndex: 'prNumbers',
      align: 'center',
      sorter: (a, b) => (a.prNumbers || '').localeCompare(b.prNumbers || ''),
      render: (text) => (text ? text.toUpperCase() : '-'),
    },
    {
      key: 'total',
      title: 'DELIVERED / QTY',
      align: 'center',
      render: (data) => {
        const delivered = data.quantityDelivered || 0;
        const total = data.totalPoQuantity || 0;
        return (
          <span>
            <span style={{ fontWeight: 600, color: '#52c41a' }}>{delivered.toLocaleString()}</span>
            <span style={{ margin: '0 4px' }}>/</span>
            <span style={{ fontWeight: 600, color: '#1890ff' }}>{total.toLocaleString()}</span>
          </span>
        );
      },
      sorter: (a, b) => {
        const aDelivered = a.quantityDelivered || 0;
        const bDelivered = b.quantityDelivered || 0;
        return aDelivered - bDelivered;
      },
    },
    {
      key: 'currency',
      title: 'CURRENCY',
      dataIndex: 'currency',
      filters: [
        { text: 'USD', value: 'USD' },
        { text: 'PHP', value: 'PHP' },
      ],
      onFilter: (value, record) => record.currency === value,
      sorter: (a, b) => (a.currency || '').localeCompare(b.currency || ''),
      align: 'center',
      render: (text) => (
        <Badge
          count={text}
          style={{
            backgroundColor: text === 'PHP' ? '#52c41a' : '#1890ff',
            fontSize: 11,
            fontWeight: 600,
            padding: '0 8px',
            borderRadius: 4,
          }}
        />
      ),
    },
    {
      key: 'date',
      title: 'DATE',
      dataIndex: 'date',
      sorter: (a, b) => moment(a.date).unix() - moment(b.date).unix(),
      align: 'center',
      render: (text) => text ? moment(text).format('MMM DD, YYYY') : '-',
    },
  ], [supplierOption, openModal]);

  return (
    <div className="po-table-container">
      <style>{tableStyles}</style>
      
      {/* Statistics Cards */}
      {poList && poList.length > 0 && (
        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
          {/* TOTAL P.O. */}
          <Col xs={12} sm={12} md={8} lg={6}>
            <Card className="po-stats-card" bodyStyle={{ padding: '16px' }} bordered={false}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className="po-stats-icon" style={{ background: '#e6f7ff' }}>
                  <Icon type="file-text" style={{ fontSize: 20, color: '#1890ff' }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#8c8c8c', fontWeight: 600, letterSpacing: '0.3px' }}>
                    TOTAL P.O.
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#1890ff' }}>
                    {stats.total.toLocaleString()}
                  </div>
                </div>
              </div>
            </Card>
          </Col>
          
          {/* TOTAL QUANTITY */}
          <Col xs={12} sm={12} md={8} lg={6}>
            <Card className="po-stats-card" bodyStyle={{ padding: '16px' }} bordered={false}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className="po-stats-icon" style={{ background: '#f6ffed' }}>
                  <Icon type="shopping-cart" style={{ fontSize: 20, color: '#52c41a' }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#8c8c8c', fontWeight: 600, letterSpacing: '0.3px' }}>
                    TOTAL QUANTITY
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#52c41a' }}>
                    {stats.totalQuantity.toLocaleString()}
                  </div>
                </div>
              </div>
            </Card>
          </Col>
          
          {/* PHP PURCHASE ORDERS */}
          <Col xs={12} sm={12} md={8} lg={6}>
            <Card className="po-stats-card" bodyStyle={{ padding: '16px' }} bordered={false}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className="po-stats-icon" style={{ background: '#f6ffed' }}>
                  <Icon type="dollar" style={{ fontSize: 20, color: '#52c41a' }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#8c8c8c', fontWeight: 600, letterSpacing: '0.3px' }}>
                    PHP P.O.
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#52c41a' }}>
                    {stats.phpCount.toLocaleString()}
                  </div>
                </div>
              </div>
            </Card>
          </Col>
          
          {/* USD PURCHASE ORDERS */}
          <Col xs={12} sm={12} md={8} lg={6}>
            <Card className="po-stats-card" bodyStyle={{ padding: '16px' }} bordered={false}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className="po-stats-icon" style={{ background: '#e6f7ff' }}>
                  <Icon type="dollar" style={{ fontSize: 20, color: '#1890ff' }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#8c8c8c', fontWeight: 600, letterSpacing: '0.3px' }}>
                    USD P.O.
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#1890ff' }}>
                    {stats.usdCount.toLocaleString()}
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {/* Table */}
      <Table
        rowKey="id"
        dataSource={poList}
        columns={columns}
        size="middle"
        style={{ marginTop: 10 }}
        pagination={{
          showSizeChanger: true,
          pageSizeOptions: ['10', '25', '50', '100'],
          showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`,
          position: 'bottom',
          defaultPageSize: 10,
        }}
        bordered
        rowClassName={(_, index) => index % 2 === 0 ? '' : 'ant-table-row-stripe'}
      />
      
      {poList && poList.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          background: '#fafafa',
          borderRadius: 10,
          marginTop: 20,
        }}>
          <Icon type="inbox" style={{ fontSize: 48, color: '#bfbfbf', marginBottom: 16 }} />
          <div style={{ fontSize: 14, color: '#8c8c8c' }}>No purchase orders found</div>
        </div>
      )}
    </div>
  );
};

PurchaseOrderTable.propTypes = {
  poList: PropTypes.arrayOf(PropTypes.object).isRequired,
  supplierOption: PropTypes.arrayOf(PropTypes.object).isRequired,
  openModal: PropTypes.func.isRequired,
};

export default PurchaseOrderTable;